import { generatePeriods, MFR_DATE_FORMAT, MFR_START_YEAR, getFirstSelectablePeriod } from '../utils/PeriodGenerator';
import { isFeatureEnabled } from '../config/feature-flags';

// MFR Form Constants
export const ACTUALS_LABEL = 'Actuals';
export const ACTUALS_VALUE = 'Actuals';
export const USD_CONSTANT_LABEL = 'USD Constant';
export const USD_NOMINAL_LABEL = 'USD Nominal';

// Currency values - new format behind feature flag, legacy format for prod
export const USD_CONSTANT_VALUE = isFeatureEnabled('USD_NOMINAL') ? 'Constant' : 'constantamount';
export const USD_NOMINAL_VALUE = isFeatureEnabled('USD_NOMINAL') ? 'USD' : 'usdamount';

export const MILLIONS_VALUE = 'Millions';

// Comparison scenario options
export const COMPARISON_SCENARIO_OPTIONS = {
  Q1G: { value: 'Guidance Q1', label: 'Q1G' },
  Q2G: { value: 'Guidance Q2', label: 'Q2G' },
  Q3G: { value: 'Guidance Q3', label: 'Q3G' },
  Q4G: { value: 'Guidance Q4', label: 'Q4G' },
  MOM: { value: 'MoM', label: 'MoM' },
  QOQ: { value: 'QoQ', label: 'QoQ' },
  YOY: { value: 'YoY', label: 'YoY' }
};

/**
 * Latest year where OP2 is finalized by Corp.
 * Update this value when Corp confirms OP2 for a new year.
 */
export const OP2_LATEST_FINALIZED_YEAR = '2025';

// Generate dynamic period data from MFR start year to current year using YYYYMM format
const periodData = generatePeriods(MFR_START_YEAR, MFR_DATE_FORMAT);

// Add 'key' property to match the expected tree structure
const addKeys = (nodes: any[]): any[] => {
  return nodes.map(node => ({
    ...node,
    key: node.value,
    children: node.children ? addKeys(node.children) : undefined
  }));
};

export const PERIOD_TREE_DATA = addKeys(periodData.children);

// Get the first selectable period as default
export const DEFAULT_PERIOD = getFirstSelectablePeriod(PERIOD_TREE_DATA);

// MFR Capability Names
export const CAPABILITY_NAMES = {
  EXECUTIVE_PNL_EXECUTIVE_SUMMARY: 'Executive P&L - Executive Summary',
  EXECUTIVE_PNL_PNL_BY_CHANNEL: 'Executive P&L - P&L by Channel',
  EXECUTIVE_PNL_PNL_BY_GL: 'Executive P&L - P&L by GL',
  EXECUTIVE_PNL_PNL_BY_MARKETPLACE: 'Executive P&L - P&L by Marketplace',
  BUSINESS_TRENDS_TOP_BOTTOM: 'Business Trends - Top and Bottom ASINs/Brands',
  COST_TO_SERVE: 'Cost to Serve',
  DETAILED_PNL: 'Detailed P&L',
  PL_SLICE: 'P&L by Slice',
  PL_DRILL_DOWN: 'P&L Drill Down',
} as const;

// MFR Step IDs (Section IDs)
export const STEP_IDS = {
  // Executive P&L
  EXECUTIVE_PNL_EXECUTIVE_SUMMARY: 'executive_pnl_executive_summary',
  EXECUTIVE_PNL_PNL_BY_CHANNEL: 'executive_pnl_pnl_by_channel',
  EXECUTIVE_PNL_PNL_BY_GL: 'executive_pnl_pnl_by_gl',
  EXECUTIVE_PNL_PNL_BY_MARKETPLACE: 'executive_pnl_pnl_by_marketplace',
  // Business Trends
  BUSINESS_TRENDS_TOP_BOTTOM_BRANDS: 'business_trends_top_bottom_brands',
  BUSINESS_TRENDS_TOP_BOTTOM_ASINS: 'business_trends_top_bottom_asins',
  // Other Capabilities
  COST_TO_SERVE: 'cost_to_serve',
  DETAILED_PNL: 'detailed_pnl',
  PL_SLICE: 'pl-slice',
  PL_DRILL_DOWN: 'pl-drill-down',
  // P&L by Slice - Dynamic children
  PNL_BY_SLICE_ASP_BAND: 'pnl_by_slice_asp_band',
  PNL_BY_SLICE_EVERYDAY_ESSENTIALS: 'pnl_by_slice_everyday_essentials',
  PNL_BY_SLICE_FC_TYPE: 'pnl_by_slice_fc_type',
  PNL_BY_SLICE_PRIME_PLAN: 'pnl_by_slice_prime_plan',
  PNL_BY_SLICE_PRIVATE_BRANDS: 'pnl_by_slice_private_brands',
  PNL_BY_SLICE_SORT_TYPE: 'pnl_by_slice_sort_type',
  PNL_BY_SLICE_IS_SNS: 'pnl_by_slice_is_sns',
  PNL_BY_SLICE_BRAND_NAME: 'pnl_by_slice_brand_name',
  PNL_BY_SLICE_ITEM_SIZE: 'pnl_by_slice_item_size',
  PNL_BY_SLICE_SIOC: 'pnl_by_slice_sioc',
  PNL_BY_SLICE_HCTP: 'pnl_by_slice_hctp',
  PNL_BY_SLICE_TEXTBOOK_TYPE: 'pnl_by_slice_textbook_type',
  PNL_BY_SLICE_AMAZON_RENEWED: 'pnl_by_slice_amazon_renewed',
  PNL_BY_SLICE_SPEED_PROGRAM: 'pnl_by_slice_speed_program',
  PNL_BY_SLICE_JP_HEAVYBULKY: 'pnl_by_slice_jp_heavybulky',
  PNL_BY_SLICE_IS_B2B: 'pnl_by_slice_is_b2b',
  PNL_BY_SLICE_AB_SECTOR: 'pnl_by_slice_ab_sector',
  PNL_BY_SLICE_AB_REVENUE_TIER: 'pnl_by_slice_ab_revenue_tier',
  PNL_BY_SLICE_AB_USER_TYPE: 'pnl_by_slice_ab_user_type',
  PNL_BY_SLICE_COUPONS: 'pnl_by_slice_coupons',
  PNL_BY_SLICE_SELLER_FULFILLED_PRIME: 'pnl_by_slice_seller_fulfilled_prime',
  PNL_BY_SLICE_SPECIAL_HANDLING_US: 'pnl_by_slice_special_handling_us',
  // P&L Drill Down - Dynamic children
  PNL_DRILL_DOWN_NET_REVENUE: 'pnl_drill_down_net_revenue',
  PNL_DRILL_DOWN_MARKETPLACE_REVENUE: 'pnl_drill_down_marketplace_revenue',
  PNL_DRILL_DOWN_NET_PPM: 'pnl_drill_down_net_ppm',
  PNL_DRILL_DOWN_VENDOR_PROFIT: 'pnl_drill_down_vendor_profit',
  PNL_DRILL_DOWN_GROSS_PROFIT: 'pnl_drill_down_gross_profit',
  PNL_DRILL_DOWN_CONTRIBUTION_PROFIT: 'pnl_drill_down_contribution_profit',
  PNL_DRILL_DOWN_CP_LESS_OPS_FIELD_FIXED: 'pnl_drill_down_cp_less_ops_field_fixed',
  PNL_DRILL_DOWN_OPERATING_INCOME: 'pnl_drill_down_operating_income',
  PNL_DRILL_DOWN_FREIGHT_IN_TOTAL: 'pnl_drill_down_freight_in_total',
  PNL_DRILL_DOWN_GMS: 'pnl_drill_down_gms',
  PNL_DRILL_DOWN_NET_SELLER_REIMBURSEMENTS: 'pnl_drill_down_net_seller_reimbursements',
  PNL_DRILL_DOWN_OPERATING_MARGIN_PCT: 'pnl_drill_down_operating_margin_pct',
  PNL_DRILL_DOWN_OPS_CS_VARIABLE_TOTAL: 'pnl_drill_down_ops_cs_variable_total',
  PNL_DRILL_DOWN_SHIPPING_COGS: 'pnl_drill_down_shipping_cogs',
  PNL_DRILL_DOWN_TOTAL_OPS_FIELD_FIXED: 'pnl_drill_down_total_ops_field_fixed',
  PNL_DRILL_DOWN_UNITS: 'pnl_drill_down_units',
} as const;

// Mapping from drill down step IDs to reporting line values
export const PNL_DRILL_DOWN_REPORTING_LINE_MAP: Record<string, string> = {
  [STEP_IDS.PNL_DRILL_DOWN_NET_REVENUE]: 'Net Revenue',
  [STEP_IDS.PNL_DRILL_DOWN_MARKETPLACE_REVENUE]: 'Marketplace Rev.',
  [STEP_IDS.PNL_DRILL_DOWN_NET_PPM]: 'Net PPM%',
  [STEP_IDS.PNL_DRILL_DOWN_VENDOR_PROFIT]: 'Vendor Profit',
  [STEP_IDS.PNL_DRILL_DOWN_GROSS_PROFIT]: 'Gross Profit',
  [STEP_IDS.PNL_DRILL_DOWN_CONTRIBUTION_PROFIT]: 'Contribution Profit',
  [STEP_IDS.PNL_DRILL_DOWN_CP_LESS_OPS_FIELD_FIXED]: 'CP Less Ops Field Fixed',
  [STEP_IDS.PNL_DRILL_DOWN_OPERATING_INCOME]: 'Operating Income',
  [STEP_IDS.PNL_DRILL_DOWN_FREIGHT_IN_TOTAL]: 'Freight InTotal',
  [STEP_IDS.PNL_DRILL_DOWN_GMS]: 'GMS',
  [STEP_IDS.PNL_DRILL_DOWN_NET_SELLER_REIMBURSEMENTS]: 'Net Seller Reimbursements',
  [STEP_IDS.PNL_DRILL_DOWN_OPERATING_MARGIN_PCT]: 'Operating Margin as % of Revenue',
  [STEP_IDS.PNL_DRILL_DOWN_OPS_CS_VARIABLE_TOTAL]: 'Ops & CS Variable Total',
  [STEP_IDS.PNL_DRILL_DOWN_SHIPPING_COGS]: 'Shipping COGS',
  [STEP_IDS.PNL_DRILL_DOWN_TOTAL_OPS_FIELD_FIXED]: 'Total Ops Field Fixed Costs (incl Depreciation)',
  [STEP_IDS.PNL_DRILL_DOWN_UNITS]: 'Units',
};
